import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CanvasserFilters } from '../types';

export interface CanvassersState {
  // Selected canvassers for bulk operations
  selectedCanvasserIds: string[];
  // Filter and search state
  filters: CanvasserFilters;
  // UI state
  isCreateModalOpen: boolean;
  isDetailsModalOpen: boolean;
  currentCanvasserId?: string;
}

const initialState: CanvassersState = {
  selectedCanvasserIds: [],
  filters: {
    filter: 'all',
    search: '',
    sort: 'display_name',
    sortDirection: 'asc',
  },
  isCreateModalOpen: false,
  isDetailsModalOpen: false,
  currentCanvasserId: undefined,
};

const canvassersSlice = createSlice({
  name: 'canvassers',
  initialState,
  reducers: {
    // Selection management
    setSelectedCanvasserIds: (state, action: PayloadAction<string[]>) => {
      state.selectedCanvasserIds = action.payload;
    },
    toggleCanvasserSelection: (state, action: PayloadAction<string>) => {
      const canvasserId = action.payload;
      const index = state.selectedCanvasserIds.indexOf(canvasserId);
      if (index > -1) {
        state.selectedCanvasserIds.splice(index, 1);
      } else {
        state.selectedCanvasserIds.push(canvasserId);
      }
    },
    clearSelection: (state) => {
      state.selectedCanvasserIds = [];
    },
    selectAllCanvassers: (state, action: PayloadAction<string[]>) => {
      state.selectedCanvasserIds = action.payload;
    },

    // Filter management
    setFilters: (state, action: PayloadAction<Partial<CanvasserFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setFilter: (state, action: PayloadAction<CanvasserFilters['filter']>) => {
      state.filters.filter = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
    },
    setSort: (
      state,
      action: PayloadAction<{
        sort: CanvasserFilters['sort'];
        sortDirection: CanvasserFilters['sortDirection'];
      }>
    ) => {
      state.filters.sort = action.payload.sort;
      state.filters.sortDirection = action.payload.sortDirection;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Modal management
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
    },
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
    },
    openDetailsModal: (state, action: PayloadAction<string>) => {
      state.isDetailsModalOpen = true;
      state.currentCanvasserId = action.payload;
    },
    closeDetailsModal: (state) => {
      state.isDetailsModalOpen = false;
      state.currentCanvasserId = undefined;
    },

    // Reset state
    resetCanvassersState: () => initialState,
  },
});

export const {
  setSelectedCanvasserIds,
  toggleCanvasserSelection,
  clearSelection,
  selectAllCanvassers,
  setFilters,
  setFilter,
  setSearch,
  setSort,
  clearFilters,
  openCreateModal,
  closeCreateModal,
  openDetailsModal,
  closeDetailsModal,
  resetCanvassersState,
} = canvassersSlice.actions;

export default canvassersSlice;
